import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, effect, inject, input, signal } from '@angular/core';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMessageService } from 'ng-zorro-antd/message';

import { Customer360Service } from './services/customer-360.service';
import { CommunicationEntry } from './models/customer-360.types';
import { BirthdaySendPanelComponent } from './components/birthday-send-panel/birthday-send-panel.component';

/**
 * Standalone communication-log panel for the Customer 360 split view.
 *
 * Lives as its own component (not just a block inside
 * `Customer360ContentComponent`) so `UserViewComponent` can render it
 * in a dedicated tab — the user asked for კომუნიკაცია to get its own
 * tab rather than being buried at the bottom of a long scroll.
 *
 * Unlike the main content component, this one calls the dedicated
 * `/reports/customers/:id/communications` endpoint instead of the
 * full `/360` bundle. That keeps the tab lightweight and also means
 * the HTTP request only fires when the user actually opens the tab,
 * because Angular's `nz-tab` destroys inactive tab content by default.
 */
@Component({
  selector: 'beautyapp-customer-360-communications',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    NzSpinModule,
    NzEmptyModule,
    NzTagModule,
    NzIconModule,
    BirthdaySendPanelComponent,
  ],
  template: `
    <nz-spin [nzSpinning]="loading()" nzSize="large">
      <section class="panel">
        <h3>კომუნიკაცია</h3>
        <!--
          beaut-308 walk finding 6. This used to sit ABOVE the heading, which
          made it read as a floating control unattached to the კომუნიკაცია
          section. Moved below the heading so it reads as part of it.
        -->
        <beautyapp-birthday-send-panel
          [userId]="userId()"
          (sent)="load(userId())"
        ></beautyapp-birthday-send-panel>
        <div *ngIf="items().length; else emptyComms" class="comm-list">
          <div class="comm-row" *ngFor="let c of items()">
            <span nz-icon [nzType]="c.channel === 'sms' ? 'mobile' : 'mail'"></span>
            <div class="comm-body">
              <div class="comm-head">
                <span class="comm-date">{{ c.sentAt }}</span>
                <nz-tag [nzColor]="statusColor(c.status)">{{ c.status || '—' }}</nz-tag>
                <span class="comm-event" *ngIf="c.eventType">{{ c.eventType }}</span>
              </div>
              <div class="comm-text">{{ c.subject || c.preview }}</div>
            </div>
          </div>
        </div>
        <ng-template #emptyComms>
          <div *ngIf="!loading()"><nz-empty></nz-empty></div>
        </ng-template>
      </section>
    </nz-spin>
  `,
  styles: [`
    .panel { background: #fff; border: 1px solid #f0f0f0; border-radius: 8px; padding: 20px; }
    .panel h3 { margin: 0 0 16px; font-size: 16px; font-weight: 600; }
    beautyapp-birthday-send-panel { display: block; margin-bottom: 16px; }
    .comm-list { display: flex; flex-direction: column; gap: 10px; }
    .comm-row { display: grid; grid-template-columns: 24px 1fr; gap: 10px; padding: 8px 0; border-bottom: 1px dashed #f0f0f0; }
    .comm-row:last-child { border-bottom: none; }
    .comm-head { display: flex; gap: 8px; align-items: center; font-size: 12px; color: #8c8c8c; }
    .comm-text { margin-top: 2px; }
  `],
})
export class Customer360CommunicationsComponent {
  userId = input.required<string>();

  private api = inject(Customer360Service);
  private msg = inject(NzMessageService);

  loading = signal(true);
  items = signal<CommunicationEntry[]>([]);

  constructor() {
    effect(() => {
      const id = this.userId();
      if (id) this.load(id);
    });
  }

  load(id: string): void {
    this.loading.set(true);
    this.api.getCommunications(id, { limit: 100 }).subscribe({
      next: (rows) => {
        this.items.set(rows ?? []);
        this.loading.set(false);
      },
      error: () => {
        this.items.set([]);
        this.loading.set(false);
        this.msg.error('კომუნიკაციების ჩატვირთვა ვერ მოხერხდა');
      },
    });
  }

  statusColor(s: string | null): string {
    if (!s) return 'default';
    const up = s.toUpperCase();
    if (up.includes('DELIVERED') || up.includes('OPENED') || up.includes('SENT')) return 'green';
    if (up.includes('FAILED') || up.includes('ERROR')) return 'red';
    return 'blue';
  }
}
